import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import {
  DownloadIncidentIssueWithCacheAsCsvResponse,
  IncidentIssueWithCache,
  IncidentIssueWithCacheSearch,
  QueryDownloadIncidentIssueWithCacheAsCsvArgs,
} from '@/models/gen/graphql';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';
import { getProperty, queryInput } from '@/utils';

import { DownloadIncidentIssueWithCacheAsCsvDocument } from '@/api/queries';
import { TypedDocumentNode } from '@apollo/client';
import graphApi from '@/api';

// Service Function Signature
type ServiceMethodPayload = ({
  startDatetime?: string;
  endDatetime?: string;
  stations?: string[];
  tripServicerIataAirlineCodes?: string[];
} & { [key in keyof IncidentIssueWithCacheSearch]?: any })[];
interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<DownloadIncidentIssueWithCacheAsCsvResponse>;
}

const incidentIssueCSVKeys: (keyof IncidentIssueWithCache)[] = [
  'crewInfo',
  'crewInvolved',
  'description',
  'dispatcherFirstName',
  'dispatcherLastName',
  'driverFirstName',
  'driverLastName',
  'driverEmployeeId',
  'incidentDatetime',
  'incidentOccurance',
  'incidentOccuranceOther',
  'incidentWitnesses',
  'managerFirstName',
  'managerLastName',
  'medicalAttentionType',
  'medicalInjuries',
  'medicalNeeded',
  'outboundDatetime',
  'outboundFlightNumber',
  'propertyDamage',
  'propertyDamageDescription',
  'recoveryInfo',
  'samsaraCameraWorking',
  'station',
  'tripFlightNumber',
  'tripScheduled',
  'tripServicerIataAirlineCode',
  'vehicleTrackingId',
];

const incidentIssueCSVColumns: Record<keyof IncidentIssueWithCache, true> = Object.fromEntries(
  incidentIssueCSVKeys.map((key) => [key, true])
) as Record<keyof IncidentIssueWithCache, true>;

// IncidentIssue CSV Service
export const { downloadIncidentIssueWithCacheCsv, useDownloadIncidentIssueWithCacheCsv } = ((): {
  downloadIncidentIssueWithCacheCsv: ServiceMethod;
  useDownloadIncidentIssueWithCacheCsv: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch] = graphApi(DownloadIncidentIssueWithCacheAsCsvDocument).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<DownloadIncidentIssueWithCacheAsCsvResponse> => {
        const query: IncidentIssueWithCacheSearch[] = payload?.map((item) => ({
          createdAt: queryInput.date([item?.startDatetime, item.endDatetime]),
          station: queryInput.orNull(item?.stations),
          tripServicerIataAirlineCode: queryInput.orNull(item?.tripServicerIataAirlineCodes),
          medicalInjuries: queryInput(item?.medicalInjuries),
        }));
        const args: QueryDownloadIncidentIssueWithCacheAsCsvArgs = {
          input: {
            query: query,
            columns: incidentIssueCSVColumns,
          },
        };
        const opts = { merge: options?.merge ? 'downloadIncidentIssueWithCacheAsCSV' : undefined };
        const res = await fn(args, opts);
        return getProperty('downloadIncidentIssueWithCacheAsCSV', res);
      }
  );
  const useService = createGraphApiHook(fetch);
  return {
    downloadIncidentIssueWithCacheCsv: fetch,
    useDownloadIncidentIssueWithCacheCsv: useService,
  };
})();
